import React from 'react';
import { Filter, Zap, Shield, Gauge, Scale } from 'lucide-react';

interface TransformerFiltersProps {
  selectedPower?: string;
  selectedVoltage?: string;
  selectedConnection?: string;
  selectedMaterial?: string;
  selectedProtection?: string;
  selectedType?: string;
  onFilterChange: (filterType: string, value: string) => void;
}

const TransformerFilters: React.FC<TransformerFiltersProps> = ({
  selectedPower = '',
  selectedVoltage = '',
  selectedConnection = '',
  selectedMaterial = '',
  selectedProtection = '',
  selectedType = '',
  onFilterChange
}) => {
  const powerOptions = ['40', '63', '100', '160', '250', '400', '630', '1000', '1600', '2500', '3150', '4000', '6300', '10000', '16000'];
  const voltageOptions = ['6/0.4', '10/0.4'];
  const connectionOptions = ['Y/Yн-0', 'D/Yн-11'];
  const materialOptions = ['алюминий', 'медь'];
  const protectionOptions = ['IP00', 'IP21/IP31'];
  const typeOptions = ['TSL', 'TSZL', 'TMG'];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <div className="flex items-center mb-4">
        <Filter className="w-5 h-5 text-primary-600 mr-2" />
        <h2 className="text-xl font-semibold">Фильтры</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Мощность */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Zap className="w-4 h-4 mr-2 text-primary-600" />
            Мощность, кВА
          </label>
          <select
            value={selectedPower}
            onChange={(e) => onFilterChange('power', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все</option>
            {powerOptions.map(power => (
              <option key={power} value={power}>{power}</option>
            ))}
          </select>
        </div>

        {/* Напряжение */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Gauge className="w-4 h-4 mr-2 text-primary-600" />
            Напряжение, кВ
          </label>
          <select
            value={selectedVoltage}
            onChange={(e) => onFilterChange('voltage', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все</option>
            {voltageOptions.map(voltage => (
              <option key={voltage} value={voltage}>{voltage}</option>
            ))}
          </select>
        </div>

        {/* Схема соединения */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Scale className="w-4 h-4 mr-2 text-primary-600" />
            Схема соединения
          </label>
          <select
            value={selectedConnection}
            onChange={(e) => onFilterChange('connection', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все</option>
            {connectionOptions.map(connection => (
              <option key={connection} value={connection}>{connection}</option>
            ))}
          </select>
        </div>

        {/* Материал обмоток */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Scale className="w-4 h-4 mr-2 text-primary-600" />
            Материал обмоток
          </label>
          <select
            value={selectedMaterial}
            onChange={(e) => onFilterChange('material', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все</option>
            {materialOptions.map(material => (
              <option key={material} value={material}>{material}</option>
            ))}
          </select>
        </div>

        {/* Степень защиты */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Shield className="w-4 h-4 mr-2 text-primary-600" />
            Степень защиты
          </label>
          <select
            value={selectedProtection}
            onChange={(e) => onFilterChange('protection', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все</option>
            {protectionOptions.map(protection => (
              <option key={protection} value={protection}>{protection}</option>
            ))}
          </select>
        </div>

        {/* Тип трансформатора */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Filter className="w-4 h-4 mr-2 text-primary-600" />
            Тип трансформатора
          </label>
          <select
            value={selectedType}
            onChange={(e) => onFilterChange('type', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все</option>
            {typeOptions.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Сброс фильтров */}
      <div className="mt-4 flex justify-end">
        <button
          onClick={() => {
            onFilterChange('power', '');
            onFilterChange('voltage', '');
            onFilterChange('connection', '');
            onFilterChange('material', '');
            onFilterChange('protection', '');
            onFilterChange('type', '');
          }}
          className="text-sm text-gray-600 hover:text-primary-600 flex items-center"
        >
          <Filter className="w-4 h-4 mr-1" />
          Сбросить все фильтры
        </button>
      </div>
    </div>
  );
};

export default TransformerFilters;
